/** Invisible Cloudflare Turnstile: one widget, a fresh single-use token per request. */

interface TurnstileApi {
  render(el: HTMLElement, opts: Record<string, unknown>): string;
  execute(el: HTMLElement | string): void;
  reset(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

// Cloudflare's "always passes, invisible" test key, for local development only.
const DEV_SITE_KEY = '1x00000000000000000000BB';
const SITE_KEY: string = import.meta.env.VITE_TURNSTILE_SITE_KEY || (import.meta.env.DEV ? DEV_SITE_KEY : '');
const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<TurnstileApi> | null = null;
let widget: { id: string; el: HTMLElement } | null = null;
let pending: { resolve(t: string): void; reject(e: Error): void } | null = null;

function loadScript(): Promise<TurnstileApi> {
  scriptPromise ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing')));
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error('turnstile script failed'));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/** Starts loading early so the first request is not delayed. */
export function preloadTurnstile() {
  if (SITE_KEY) loadScript().catch(() => {});
}

export async function getTurnstileToken(): Promise<string> {
  if (!SITE_KEY) throw new Error('VITE_TURNSTILE_SITE_KEY is not set');
  const ts = await loadScript();
  if (!widget) {
    const el = document.createElement('div');
    el.style.cssText = 'position:fixed;bottom:8px;left:8px;z-index:50';
    document.body.appendChild(el);
    const id = ts.render(el, {
      sitekey: SITE_KEY,
      execution: 'execute',
      appearance: 'interaction-only',
      callback: (token: string) => pending?.resolve(token),
      'error-callback': () => pending?.reject(new Error('turnstile error')),
    });
    widget = { id, el };
  } else {
    ts.reset(widget.id);
  }
  const w = widget;
  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('turnstile timeout')), 20_000);
    pending = {
      resolve: (t) => {
        clearTimeout(timer);
        pending = null;
        resolve(t);
      },
      reject: (e) => {
        clearTimeout(timer);
        pending = null;
        reject(e);
      },
    };
    ts.execute(w.el);
  });
}
